import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState({ payment_status: "checking", status: "open" });
  const [attempts, setAttempts] = useState(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async (n) => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") return;
        if (n >= 8) return; // ~16s
        timer = setTimeout(() => poll(n + 1), 2000);
        setAttempts(n + 1);
      } catch {
        timer = setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  const paid = status.payment_status === "paid";
  const expired = status.status === "expired";

  if (paid) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
        <CheckCircle size={72} weight="duotone" className="text-[#FF3B30] mx-auto" />
        <div className="section-eyebrow justify-center mt-4">Payment Received</div>
        <h1 className="font-display text-5xl uppercase mt-3" data-testid="checkout-status-paid">The King thanks you.</h1>
        <p className="text-dim mt-3">Your order is confirmed. A receipt was sent by Stripe.</p>
        {status.amount_total != null && (
          <p className="text-dim mt-2">Total: <span className="text-white">${(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}</span></p>
        )}
        <Link to="/shop" className="btn-red inline-block mt-8" data-testid="back-to-shop">Continue Shopping</Link>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
        <XCircle size={72} weight="duotone" className="text-[#FF3B30] mx-auto" />
        <h1 className="font-display text-5xl uppercase mt-3" data-testid="checkout-status-expired">Session Expired</h1>
        <p className="text-dim mt-3">Your checkout session timed out. Please try again.</p>
        <Link to="/cart" className="btn-red inline-block mt-8">Back to Cart</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      <Clock size={72} weight="duotone" className="text-[#FFD700] mx-auto animate-pulse" />
      <h1 className="font-display text-5xl uppercase mt-3" data-testid="checkout-status-checking">Confirming Payment…</h1>
      <p className="text-dim mt-3">Hold tight, we&apos;re verifying with Stripe. (attempt {attempts + 1})</p>
    </div>
  );
}
